import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useGetOrdersQuery,
  useSaveTransactionMutation,
  useAddRepeatOrderMutation,
  useAddOrderPreferencesMutation
} from "../../../services/orderService";
import OrderCard from "../../../components/orderCard/OrderCard";
import LoadingSpinner from "../../../components/loadingSpinner/LoadingSpinner";
import TaskCard, { TASK_STATUS } from "../../../components/taskCard/TaskCard";
import useQuery from "../../../hooks/useQuery";
import parseError from "../../../utils/errorUtils";
import { useLoggerUtils } from "../../../utils/loggerUtils";
import {
  APPROVED_STATUS,
  FAILED_STATUS,
  REJECTED_STATUS,
} from "../../../constants/orderStatus";

function OrdersPage() {
  const navigate = useNavigate();
  const query = useQuery();
  const { info } = useLoggerUtils();

  const [isInnerLoading, setIsInnerLoading] = useState(false);
  const {
    data: ordersData,
    isLoading: ordersLoading,
    refetch: refetchOrders,
  } = useGetOrdersQuery();
  const [saveTransactionMutation] = useSaveTransactionMutation(); // Save external stripe transactions
  const [addRepeatOrder] = useAddRepeatOrderMutation();
  const [addOrderPreferences] = useAddOrderPreferencesMutation();

  const saveTransactionAPI = async (sessionId, orderId, paymentType) => {
    try {
      setIsInnerLoading(true);
      const res = await saveTransactionMutation({
        sessionId: sessionId ? sessionId : "",
        orderItemId: orderId,
        mode: paymentType,
      });

      if (res.error) {
        toast.error(parseError(res.error, "Couldn't place order"));
      } else {
        localStorage.removeItem("repeatSuccess");
        localStorage.removeItem("orderItemId");
        localStorage.removeItem("sessionId");
        await refetchOrders();
        window.history.pushState({}, "", "/user/order");
      }
    } catch (error) {
      console.log("Stripe Save Transaction : ", error);
    } finally {
      toast.success("Successfully placed order");
      setIsInnerLoading(false);
    }
  };

  // Save stripe related transaction
  useEffect(() => {
    const checkout = query.get("checkout");
    const isChangeOrder = query.get("is_change_order");
    const sessionId = localStorage.getItem("sessionId");
    const subscribed = localStorage.getItem("subscribed");

    if(checkout === "complete") handleAddRepeatOrder(checkout, isChangeOrder, sessionId, subscribed);
  }, [query]);

  const handleAddRepeatOrder = async (checkout, isChangeOrder, sessionId, subscribed) => {

    const repeatOrderData = localStorage.getItem("AddRepeatOrder");
    const changeOrderData = JSON.parse(localStorage.getItem("isChangeOrder"));

    if(repeatOrderData){
      try {
        const res = await addRepeatOrder({
          type: "BOOKING",
          values: JSON.parse(repeatOrderData),
        });
        if (res.error) {
          toast.error(parseError(res.error, "Couldn't place order"));
        } else {
          // handle success
          if (isChangeOrder) {
            try {
              const data = {
                ...changeOrderData,
                orderItemId: res.data.message
              };
  
              await handleChangeOrder(data);
            } catch (error) {
              info("handleAddRepeatOrder - catch: " + JSON.stringify(error));
              console.error(error);
            }
          }
          localStorage.removeItem("AddRepeatOrder");
          localStorage.removeItem("AppointmentData");

          if (checkout === "complete" && res?.data?.message && sessionId) {
            saveTransactionAPI(sessionId, res.data.message, "PAYMENT");
          }
      
          if (checkout === "complete" && subscribed === "paid") {
            saveTransactionAPI(sessionId, res?.data?.message, "SUBSCRIPTION");
            localStorage.removeItem("repeatSuccess");
            localStorage.removeItem("subscribed");
          }

        }
      } catch (e) {
        console.error(e);
      }
    }
  };

  const handleChangeOrder = async (orderData) => {
    try {
      const res = await addOrderPreferences({
        values: orderData,
      });
      if (res.error) {
        info("handleAddRepeatOrder - error: " + JSON.stringify(res));
        toast.error(parseError(res.error, "Couldn't place order"));
      } else {
        localStorage.removeItem("isChangeOrder");
      }
    } catch (e) {
      info("handleAddRepeatOrder - catch: " + JSON.stringify(e));
      console.error(e);
    }
  };

  const handleOrderAction = (id) => {
    navigate(`/user/order/${id}`);
  };

  const activeOrders =
    ordersData?.content
      .filter(
        (order) =>
          !(
            REJECTED_STATUS.includes(order.status) ||
            APPROVED_STATUS.includes(order.status) ||
            FAILED_STATUS.includes(order.status)
          )
      )
      .sort((a, b) => b.id - a.id) || [];

  const pastOrders =
    ordersData?.content
      .filter(
        (order) =>
          REJECTED_STATUS.includes(order.status) ||
          FAILED_STATUS.includes(order.status) ||
          APPROVED_STATUS.includes(order.status)
      )
      .sort((a, b) => b.id - a.id) || [];

  if (ordersLoading || isInnerLoading) {
    return (
      <div className="mt-5 text-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="pt-6 pt-lg-0 pb-5 px-xl-6 px-xxl-12">
      <h2 className="fw-bold text-primary">Orders</h2>

      <h3 className="fw-bold mt-7">Active</h3>
      {activeOrders.length > 0 ? (
        activeOrders.map((content, idx) => (
          <div className="mt-4" key={idx}>
            <OrderCard onClickAction={handleOrderAction} orderData={content} />
          </div>
        ))
      ) : (
        <TaskCard
          status={TASK_STATUS.ALERT}
          title="No active orders"
          subTitle="No active orders at the moment"
        />
      )}
      <h3 className="fw-bold mt-7">Past orders</h3>
      {pastOrders.length > 0 ? (
        pastOrders.map((content, idx) => (
          <div className="mt-4" key={idx}>
            <OrderCard onClickAction={handleOrderAction} orderData={content} />
          </div>
        ))
      ) : (
        <TaskCard
          status={TASK_STATUS.ALERT}
          title="No past orders"
          subTitle="No past orders at the moment"
        />
      )}
    </div>
  );
}

export default OrdersPage;
